import { div } from './dom';

export enum DropdownOptionType {
  Selection,
  Divider,
}

interface SelectedFunc<T> {
  (selected_option: DropdownOption<T>): boolean;
}

interface DropdownCallBack<T> {
  (dropdown_option: DropdownOption<T>): void;
}

export class DropdownState {

  static menu_focused: boolean = false;
  static key_nav_in_progress: boolean = false;
  static key_nav_timer: number = 0;

  static setMenuFocused(value: boolean) {
    // delay clearing menu focus, to prevent a single click registering
    // as both a focus clear and a non-menu_focused click
    if (value === false) {
      setTimeout(function () {
        DropdownState.menu_focused = value;
      }, 200);
    } else {
      DropdownState.menu_focused = value;
    }
  }

  static setKeyNavigation(value: boolean) {
    if (value === false) {
      DropdownState.key_nav_timer = window.setTimeout(function () {
        DropdownState.key_nav_in_progress = false;
      }, 200);
    } else {
      clearTimeout(DropdownState.key_nav_timer);
      DropdownState.key_nav_in_progress = true;
    }
  }
}

export class DropdownOption<T> {
  label: string = "";
  value: T;
  type: DropdownOptionType = DropdownOptionType.Selection;
  keyboard_shortcut: string | null = null;
  selected: boolean = false;
  href?: string;

  constructor(label: string, value: T, type?: DropdownOptionType, keyboard_shortcut?: string, href?: string) {
    this.label = label;
    this.value = value;
    if (type) {
      this.type = type;
    }
    if (keyboard_shortcut) {
      this.keyboard_shortcut = keyboard_shortcut;
    }
    if (href !== undefined) {
      this.href = href;
    }
  }
}

export function viewSelector<T>(label_string: string, selected_option: string, options: DropdownOption<T>[], cb: DropdownCallBack<T>): HTMLDivElement {
  const d = div("view-attribute");
  const label = div("view-attribute-label");
  label.innerHTML = label_string;
  const dropdown = viewDropdown(selected_option, options, cb);
  d.appendChild(label);
  d.appendChild(dropdown);
  return d;
}

function viewDropdownInputElement(selected_option: string): HTMLInputElement {
  const input = document.createElement("input");
  input.classList.add("dropdown-value", "noselect");
  input.setAttribute("readonly", "true");
  input.value = selected_option;
  input.innerHTML = selected_option;
  return input;
}

function viewDropdownOption<T>(options_el: HTMLElement, option: DropdownOption<T>, cb: DropdownCallBack<T>): HTMLElement {
  const a = document.createElement("a");
  if (option.selected) {
    a.classList.add("selected");
  }

  if (option.href !== undefined) {
    a.classList.add("link");
  }

  a.innerHTML = option.label;

  if (option.keyboard_shortcut) {
    const k = document.createElement("span");
    k.innerHTML = option.keyboard_shortcut;
    a.appendChild(k);
  }

  a.onmouseenter = function () {
    if (DropdownState.key_nav_in_progress) {
      return;
    }
    for (let i = 0; i < options_el.childNodes.length; i++) {
      const child = options_el.childNodes[i] as HTMLElement;
      if (child.classList.contains("hover")) {
        child.classList.remove("hover");
        break;
      }
    }
    a.classList.add("hover");
  };
  a.onmouseleave = function () {
    if (DropdownState.key_nav_in_progress) {
      return;
    }
    a.classList.remove("hover");
  };

  a.onmousedown = function (e) {
    e.preventDefault();
    e.stopPropagation();
    cb(option);
  };

  return a;
}

export function viewDropdownOptions<T>(selected_option: string, options: DropdownOption<T>[], cb: DropdownCallBack<T>, selected_function?: SelectedFunc<T>):
  HTMLDivElement {
  const options_wrapper = div("dropdown-options-wrapper");
  const options_el = div("dropdown-options");

  for (let i = 0; i < options.length; i++) {
    if (options[i].type === DropdownOptionType.Selection) {
      if (selected_function) {
        options[i].selected = selected_function(options[i]);
      } else {
        options[i].selected = options[i].label === selected_option;
      }
      const a = viewDropdownOption(options_el, options[i], cb);
      options_el.appendChild(a);
    } else if (options[i].type === DropdownOptionType.Divider) {
      const divider = div("dropdown-divider");
      const rule = div("rule");
      divider.appendChild(rule);
      if (options[i].label !== undefined && options[i].label.length > 0) {
        const span = document.createElement("span");
        span.innerHTML = options[i].label;
        divider.appendChild(span);
      }
      options_el.appendChild(divider);
    }
  }
  options_wrapper.appendChild(options_el);
  return options_wrapper;
}

export function viewDropdown<T>(selected_option: string, options: DropdownOption<T>[], cb: DropdownCallBack<T>, selected_function?: SelectedFunc<T>): HTMLDivElement {
  const class_name = "view-attribute-dropdown";
  const dropdown = div(class_name);
  const input = viewDropdownInputElement(selected_option);
  const options_wrapper = viewDropdownOptions(selected_option, options, function (option: DropdownOption<T>) {
    input.value = option.label;
    input.innerHTML = option.label;
    cb(option);
  }, selected_function);
  const options_el = options_wrapper.getElementsByClassName("dropdown-options")[0];
  const MAX_TOP = 45;
  const PADDING = 5;

  const adjustOptionsWrapper = function () {
    const wrapper_rect = options_wrapper.getBoundingClientRect();
    const options_rect = options_el.getBoundingClientRect();
    const input_rect = input.getBoundingClientRect();
    if ((options_rect.bottom < window.innerHeight) && (options_rect.bottom > wrapper_rect.bottom)) {
      options_wrapper.style.maxHeight = (options_rect.bottom - wrapper_rect.top) + "px";
    }
    if ((options_rect.bottom > window.innerHeight) && (wrapper_rect.top > MAX_TOP)) {
      const top = options_wrapper.style.top;
      let wt = 0;
      if (top) {
        wt = Number(top.slice(0, -2));
      }
      let step = options_wrapper.scrollTop;
      let new_offset = wt - step;
      const max_offset = MAX_TOP - input_rect.top;
      let hit_top: boolean = false;
      if (new_offset < max_offset) {
        new_offset = max_offset;
        step = new_offset - max_offset;
        hit_top = true;
      }
      if (new_offset > wt) {
        return; //this happens because of the stupid bounce in safari
      }
      options_wrapper.style.top = (new_offset) + "px";
      options_wrapper.scrollTop = 0;
      if (hit_top) { //we have a maxheight and step is 0 because it hit the top so get the full height using MAX_TOP
        options_wrapper.style.maxHeight = (window.innerHeight - MAX_TOP - PADDING) + "px";
      } else {
        let mh = 0;
        if (options_wrapper.style.maxHeight) {
          mh = Number(options_wrapper.style.maxHeight.slice(0, -2));
        }
        options_wrapper.style.maxHeight = (mh + step) + "px";
      }
    } else if (wrapper_rect.top <= MAX_TOP && options_rect.bottom > window.innerHeight) { //if it hits the top exactly on a scroll/nav
      options_wrapper.style.maxHeight = (window.innerHeight - wrapper_rect.top - PADDING) + "px";
    }
  };

  function openDropdown() {
    const right_panel = document.getElementById("right_col");
    if (right_panel) {
      right_panel.classList.add("no-scroll");
    }
    options_wrapper.style.top = "";
    options_wrapper.style.maxHeight = "";
    options_wrapper.classList.add("active");
    let wrapper_rect = options_wrapper.getBoundingClientRect();
    let options_rect = options_el.getBoundingClientRect();
    const orig_top = wrapper_rect.top;
    let child_top = 0;
    for (let i = 0; i < options_el.childNodes.length; i++) {
      const child = options_el.childNodes[i] as HTMLElement;
      if (child.classList.contains("selected")) {
        child.classList.add("hover");
        let child_rect = child.getBoundingClientRect();
        let new_top = orig_top - child_rect.top;
        if (new_top + wrapper_rect.top > MAX_TOP) {
          options_wrapper.style.top = (new_top) + "px";
        } else {
          new_top = MAX_TOP - wrapper_rect.top;
          options_wrapper.style.top = (new_top) + "px";
          child_rect = child.getBoundingClientRect();
          child_top = child_rect.top;
        }
        break;
      }
    }
    wrapper_rect = options_wrapper.getBoundingClientRect();
    options_rect = options_el.getBoundingClientRect();
    if (wrapper_rect.bottom > window.innerHeight) {
      let h;
      if (options_rect.bottom < window.innerHeight) {
        h = options_rect.bottom - wrapper_rect.top - PADDING;
      } else {
        h = window.innerHeight - wrapper_rect.top - PADDING;
      }
      const need_to_scroll = child_top - orig_top;
      const room_to_scroll = options_wrapper.scrollHeight - h;
      if (need_to_scroll > room_to_scroll) {
        h = h - (need_to_scroll - room_to_scroll);
      }
      options_wrapper.style.maxHeight = h + "px";
    }
    options_wrapper.scrollTop = child_top - orig_top;
  }

  function keyNavDropdown(e: KeyboardEvent) {
    DropdownState.setKeyNavigation(true);
    let focused: HTMLElement | null;
    focused = null;
    for (let i = 0; i < options_el.childNodes.length; i++) {
      const child = options_el.childNodes[i] as HTMLElement;
      if (child.classList.contains("hover")) {
        child.classList.remove("hover");
        if (!focused) {
          let next_idx = -1;
          if (e.keyCode === 38) {
            next_idx = i - 1;
          } else {
            next_idx = i + 1;
            i++;
          }
          if (next_idx >= 0 && next_idx <= options_el.childNodes.length - 1) {
            focused = options_el.childNodes[next_idx] as HTMLElement;
          } else {
            focused = child;
          }
        }
      }
    }
    if (!focused) {
      focused = options_el.childNodes[0] as HTMLElement;
      for (let i = 0; i < options_el.childNodes.length; i++) {
        const child = options_el.childNodes[i] as HTMLElement;
        if (child.classList.contains("selected")) {
          focused = child;
          focused.classList.add("hover");
        }
      }
    }
    focused.classList.add("hover");
    const rect = focused.getBoundingClientRect();

    if (rect.bottom >= window.innerHeight) {
      options_wrapper.scrollBy(0, (rect.bottom - window.innerHeight) + 2);
      adjustOptionsWrapper();
    }

    const wrapper_rect = options_wrapper.getBoundingClientRect();
    if (rect.top < wrapper_rect.top) {
      options_wrapper.scrollBy(0, rect.top - wrapper_rect.top);
      adjustOptionsWrapper();
    }
  }

  input.onclick = function () {
    DropdownState.setMenuFocused(true);
    openDropdown();
  };
  input.onblur = function () {
    DropdownState.setMenuFocused(false);
    const right_panel = document.getElementById("right_col");
    if (right_panel) {
      right_panel.classList.remove("no-scroll");
    }
    options_wrapper.classList.remove("active");
  };

  dropdown.appendChild(input);
  options_wrapper.appendChild(options_el);
  dropdown.appendChild(options_wrapper);

  input.onkeydown = function (e) {
    if (e.keyCode === 27) {
      input.blur(); // close dropdown
      input.focus(); // refocus so keyboard to open dropdown works
    }
    if (e.keyCode === 13) {
      for (let i = 0; i < options_el.childNodes.length; i++) {
        const child = options_el.childNodes[i] as HTMLElement;
        if (child.classList.contains("hover")) {
          child.click();
          break;
        }
      }
    }
    if (e.keyCode === 38 || e.keyCode === 40) { //up down
      if (options_wrapper.classList.contains("active")) {
        keyNavDropdown(e);
      } else {
        openDropdown();
      }
    }
  };

  input.onkeyup = function (e) {
    if (e.keyCode === 38 || e.keyCode === 40) {
      DropdownState.setKeyNavigation(false);
    }
  };

  options_wrapper.onwheel = function () {
    adjustOptionsWrapper();
  };

  return dropdown;
}
